// ============================================
// Halftone Post-Process Effect
// ============================================
// Print-press dot screens: CMY-style rotated color screens or classic
// ink-on-paper mono. Comic books, newspapers, pop-art.

import type { IRenderContext } from '../../core/IRenderContext';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import { HALFTONE_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Halftone post-processing effect
 */
export class HalftoneEffect extends PostProcessEffect {
  readonly type = 'halftone';
  readonly displayName = 'Halftone';

  // Parameters
  cellSize = 8.0;
  angle = 0.0;
  sharpness = 4.0;
  mode = 0;          // 0 = color screens, 1 = mono ink-on-paper
  inkR = 0.1; inkG = 0.08; inkB = 0.12;
  paperR = 0.98; paperG = 0.96; paperB = 0.9;

  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = HALFTONE_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      { name: 'cellSize', type: 'float', min: 3, max: 32, default: 8.0, description: 'Dot cell size (px)' },
      { name: 'angle', type: 'float', min: 0, max: 3.14159, default: 0.0, description: 'Screen rotation' },
      { name: 'sharpness', type: 'float', min: 0.5, max: 8, default: 4.0, description: 'Dot edge hardness' },
      { name: 'mode', type: 'int', min: 0, max: 1, default: 0, description: 'Mode (0=color, 1=mono)' },
      { name: 'inkR', type: 'float', min: 0, max: 1, default: 0.1, description: 'Ink R (mono)' },
      { name: 'inkG', type: 'float', min: 0, max: 1, default: 0.08, description: 'Ink G (mono)' },
      { name: 'inkB', type: 'float', min: 0, max: 1, default: 0.12, description: 'Ink B (mono)' },
      { name: 'paperR', type: 'float', min: 0, max: 1, default: 0.98, description: 'Paper R (mono)' },
      { name: 'paperG', type: 'float', min: 0, max: 1, default: 0.96, description: 'Paper G (mono)' },
      { name: 'paperB', type: 'float', min: 0, max: 1, default: 0.9, description: 'Paper B (mono)' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'cellSize': return this.cellSize;
      case 'angle': return this.angle;
      case 'sharpness': return this.sharpness;
      case 'mode': return this.mode;
      case 'inkR': return this.inkR;
      case 'inkG': return this.inkG;
      case 'inkB': return this.inkB;
      case 'paperR': return this.paperR;
      case 'paperG': return this.paperG;
      case 'paperB': return this.paperB;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'cellSize': this.cellSize = Math.max(2, Math.min(64, value)); break;
      case 'angle': this.angle = value; break;
      case 'sharpness': this.sharpness = Math.max(0.5, Math.min(8, value)); break;
      case 'mode': this.mode = Math.floor(Math.max(0, Math.min(1, value))); break;
      case 'inkR': this.inkR = Math.max(0, Math.min(1, value)); break;
      case 'inkG': this.inkG = Math.max(0, Math.min(1, value)); break;
      case 'inkB': this.inkB = Math.max(0, Math.min(1, value)); break;
      case 'paperR': this.paperR = Math.max(0, Math.min(1, value)); break;
      case 'paperG': this.paperG = Math.max(0, Math.min(1, value)); break;
      case 'paperB': this.paperB = Math.max(0, Math.min(1, value)); break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const halftoneWgsl = getCompiledShader('postprocess/halftone');
    this.shader = ctx.createShader(halftoneWgsl, halftoneWgsl);

    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'halftone-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'halftone-ldr',
    });
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      cellSize: this.cellSize,
      angle: this.angle,
      sharpness: this.sharpness,
      mode: this.mode,
      // Stack/effect intensity doubles as the wet mix with the source image.
      blend: intensity,
      _pad0: 0,
      _pad1: 0,
      _pad2: 0,
      inkColor: [this.inkR, this.inkG, this.inkB],
      _pad3: 0,
      paperColor: [this.paperR, this.paperG, this.paperB],
      _pad4: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }

  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
